/**
 * Birim belgeleri — bir organizasyon biriminin ortak arşivi.
 *
 * Neden ayrı bir katman: kurumsal mevzuat, iç prosedür ve sektör raporları
 * tek bir personaya değil bir BİRİME aittir. Persona bazlı arşive
 * yüklenseydi, aynı departmandaki her uzman için ayrı ayrı yüklenmesi ve
 * her güncellemede N kez tazelenmesi gerekirdi.
 *
 * Miras: buraya yüklenen belge, bu birimdeki VE tüm alt birimlerdeki her
 * personaya otomatik açılır (gerçek organizasyonlarda bir departmanın
 * direktifi altındaki ekipleri bağlar). Kopya tutulmaz — kaynak tek yerde,
 * dolayısıyla "hangi sürüm güncel?" sorusu hiç doğmaz.
 */
import { useCallback, useEffect, useState } from "react";
import {
  COLLECTION_KIND_LABELS,
  type MemoryCollection,
  type PersonaUnit,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  createUnitCollection,
  listUnitCollections,
  searchUnit,
} from "../../transport/personaLibraryClient";
import { CollectionCard, NewCollectionModal } from "./ArchiveTab";
import { LibrarySearchPanel } from "./LibrarySearchPanel";
import { Btn, Chip, EmptyState, ErrorBanner, Modal, SectionTitle } from "./ui";

export function UnitDocumentsModal({
  unit,
  unitPath,
  onClose,
  onChanged,
}: {
  unit: PersonaUnit;
  /** Kökten yaprağa çözünmüş yol — başlıkta bağlam vermek için. */
  unitPath: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [own, setOwn] = useState<MemoryCollection[]>([]);
  const [inherited, setInherited] = useState<MemoryCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUnitCollections(unit.id);
      setOwn(data.collections);
      setInherited(data.inherited);
      setError(null);
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Bölümler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [unit.id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${unit.name} — Birim Belgeleri`}
      subtitle={
        unitPath
          ? `${unitPath} · Buraya yüklenen belgeler bu birimdeki ve alt birimlerdeki ${unit.totalCount} personaya otomatik açılır.`
          : undefined
      }
      width="max-w-3xl"
      footer={
        <Btn tone="ghost" onClick={onClose}>
          Kapat
        </Btn>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {(own.some((c) => c.chunkCount > 0) || inherited.some((c) => c.chunkCount > 0)) && (
          <div className="space-y-2 rounded-xl border border-white/[0.07] bg-white/[0.015] p-3.5">
            <SectionTitle
              title="Birim Havuzunda Ara"
              hint="Bu birimin kendi bölümleri + üst birimlerden miras aldıklarının TAMAMINDA anlamsal (vektör) arama — belgeyi açmadan içeriğini doğrulamak için."
            />
            <LibrarySearchPanel search={(q) => searchUnit(unit.id, q)} />
          </div>
        )}

        <SectionTitle
          title={`Bu Birimin Bölümleri (${own.length})`}
          hint="Mevzuat, iç prosedür, sektör raporu gibi birimin tamamını ilgilendiren belgeler."
          action={
            <Btn size="sm" tone="outline" onClick={() => setCreating(true)}>
              + Bölüm Ekle
            </Btn>
          }
        />

        {loading ? (
          <p className="py-6 text-center text-[12px] text-slate-500">Yükleniyor…</p>
        ) : own.length === 0 ? (
          <EmptyState
            title="Bu birimde henüz belge yok"
            description="Bir bölüm oluşturup birimin ortak belgelerini yükle. Alt birimlerdeki personalar da bunlara erişebilecek — her birine ayrı yüklemen gerekmez."
            action={
              <Btn tone="primary" onClick={() => setCreating(true)}>
                İlk Bölümü Oluştur
              </Btn>
            }
          />
        ) : (
          <div className="space-y-2">
            {own.map((c) => (
              <CollectionCard
                key={c.id}
                collection={c}
                onChanged={() => {
                  void load();
                  onChanged();
                }}
                onError={setError}
              />
            ))}
          </div>
        )}

        {inherited.length > 0 && (
          <div className="space-y-2 border-t border-white/[0.07] pt-4">
            <SectionTitle
              title={`Üst Birimlerden Gelenler (${inherited.length})`}
              hint="Bu birimin personaları bunlara da erişir. Düzenleme sahibi birimde yapılır."
            />
            <div className="space-y-1.5">
              {inherited.map((c) => (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.015] px-3 py-2"
                >
                  <span className="text-[12px] text-slate-300">{c.name}</span>
                  <Chip tone="cyan">miras</Chip>
                  <Chip>{COLLECTION_KIND_LABELS[c.kind]}</Chip>
                  <span className="ml-auto text-[10.5px] text-slate-600">
                    {c.docCount} belge · {c.chunkCount} parça
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <NewCollectionModal
        open={creating}
        allowStyle={false}
        onClose={() => setCreating(false)}
        onCreate={async (payload) => {
          try {
            await createUnitCollection(unit.id, payload);
            await load();
            onChanged();
            setCreating(false);
          } catch (err) {
            setError(
              err instanceof LibraryApiError ? err.message : "Bölüm oluşturulamadı.",
            );
          }
        }}
      />
    </Modal>
  );
}
